/**
 * Whether a Notification Setup row is on — the single definition the row's On
 * switch, the section's "X of Y on" count and the hook's staging rules share.
 *
 * Kept in one place on purpose: the switch, the header count and Enable all
 * used to each read `config.enabled` on their own, so a retired event could
 * read On, be counted On and be bulk-enabled while the platform never sent it
 * (CR-7770), and a row with every channel unticked read On while it delivered
 * nothing at all (CR-7771).
 *
 * @link ./useNotificationSetup for the staging rules that call these.
 */
import type {
  NotificationChannel,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { isEmailSilenced, resolveFrequency, type EventConfig, type SetupEvent } from './types';

/** Tooltip and accessible text on a retired row's locked On switch. */
export const RETIRED_SWITCH_TEXT = 'Retired – this event is no longer sent';

/**
 * Whether this event may be switched on at all.
 *
 * A retired template (`is_active=false`) is never sent, whatever a tenant
 * override says, and the API refuses `is_enabled=true` for it with
 * `event_retired`. The grid must not offer what the server will refuse.
 *
 * @param event - the grid row.
 * @returns false for a retired event, true for every live one.
 */
export const canTurnOn = (event: SetupEvent): boolean => event.isActive;

/**
 * The ticked channels that can actually deliver at the row's cadence.
 *
 * Only shipped channels count, and the cadence rule is the grid's existing
 * one ({@link isEmailSilenced}): at a frequency of Off only Bell delivers,
 * whatever the Email and Digest ticks say. The cadence resolves the way the
 * backend does — the row's own, else the tenant default, else the platform's.
 *
 * @param event - the grid row.
 * @param config - the config the row renders.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns the channels that would deliver, in column order.
 */
export const deliverableChannels = (
  event: SetupEvent,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): NotificationChannel[] => {
  const silenced = isEmailSilenced(
    resolveFrequency(config.frequency, tenantDefault, event.platformFrequency),
  );
  return event.availableChannels.filter(
    (channel) => config.channels.includes(channel) && (channel === 'bell' || !silenced),
  );
};

/**
 * Whether this row reads On: live, switched on, and with at least one channel
 * that can deliver.
 *
 * A retired row reads Off even when a stale override from before it was
 * retired still says `is_enabled=true`, and a row with nothing left to deliver
 * reads Off even though it is switched on — the backend sends it nowhere, so
 * On would lie either way.
 *
 * @param event - the grid row.
 * @param config - the config the row renders (staged edit, reset or server state).
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns true when the row will actually notify someone.
 */
export const isEventOn = (
  event: SetupEvent,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): boolean =>
  canTurnOn(event)
  && config.enabled
  && deliverableChannels(event, config, tenantDefault).length > 0;

/**
 * The config a row takes when it is switched On.
 *
 * Switching On must never leave a row reading On while delivering nothing, so
 * a row with no deliverable channel gets its shipped channels back; if even
 * those cannot deliver at its cadence, it goes back to inheriting, and if the
 * inherited cadence is silent too, it is pinned to its own platform cadence.
 *
 * @param event - the grid row.
 * @param config - its current config.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns the config to stage. Unchanged apart from `enabled` whenever the
 *   row already has a channel that delivers.
 */
export function switchedOn(
  event: SetupEvent,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): EventConfig {
  const delivers = (candidate: EventConfig): boolean =>
    deliverableChannels(event, candidate, tenantDefault).length > 0;
  const on: EventConfig = { ...config, enabled: true };
  if (delivers(on)) return on;
  const shipped: EventConfig = { ...on, channels: event.defaults.channels };
  if (delivers(shipped)) return shipped;
  const inherited: EventConfig = { ...shipped, frequency: event.defaults.frequency };
  if (delivers(inherited)) return inherited;
  return { ...shipped, frequency: event.platformFrequency };
}

/** What a topic row switches across its events: the bell, or any mail channel. */
export type TopicChannel = 'bell' | 'mail';

const onTopicChannel = (channel: NotificationChannel, topicChannel: TopicChannel): boolean =>
  (channel === 'bell') === (topicChannel === 'bell');

/**
 * Whether a topic switch can change this event.
 *
 * Retired events are never sent, required events stay on whatever a topic says,
 * and an event that does not ship the channel has nothing to switch. Nor can a
 * topic's Email box wake a row whose own and inherited cadence are both Off: it
 * could not make the row deliver, so the box would stay mixed however often it
 * was clicked. That row is set from inside the topic.
 *
 * @param event - the grid row.
 * @param topicChannel - the switch in question.
 * @param config - the config the row renders.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns true when the topic switch applies to this row.
 */
export const topicControls = (
  event: SetupEvent,
  topicChannel: TopicChannel,
  config: EventConfig,
  tenantDefault: NotificationTenantDefaultFrequency,
): boolean => {
  const applies = canTurnOn(event)
    && event.canDisable
    && event.availableChannels.some((channel) => onTopicChannel(channel, topicChannel));
  if (!applies || topicChannel === 'bell') return applies;
  const silent = (frequency: EventConfig['frequency']): boolean =>
    isEmailSilenced(resolveFrequency(frequency, tenantDefault, event.platformFrequency));
  return !silent(config.frequency) || !silent(null);
};

/**
 * What a topic's switch reads, and how many of its rows it can change.
 *
 * It reads the rows it can change, so a required event that stays on does not
 * hold a switched-off topic at "mixed". A topic it can change nothing in reads
 * its live rows instead, and stays locked.
 *
 * @param events - the topic's rows.
 * @param configFor - the config each row renders.
 * @param topicChannel - the switch in question.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns the checkbox state plus the number of rows it can change.
 */
export function topicChannelState(
  events: SetupEvent[],
  configFor: (event: SetupEvent) => EventConfig,
  topicChannel: TopicChannel,
  tenantDefault: NotificationTenantDefaultFrequency,
): { checked: boolean | 'indeterminate'; editable: number } {
  const editable = events.filter((event) =>
    topicControls(event, topicChannel, configFor(event), tenantDefault));
  const basis = editable.length
    ? editable
    : events.filter(
      (event) =>
        canTurnOn(event)
        && event.availableChannels.some((channel) => onTopicChannel(channel, topicChannel)),
    );
  const on = basis.filter((event) => {
    const config = configFor(event);
    return config.enabled && deliverableChannels(event, config, tenantDefault).some(
      (channel) => onTopicChannel(channel, topicChannel),
    );
  }).length;
  const checked = on === 0 ? false : on === basis.length ? true : 'indeterminate';
  return { checked, editable: editable.length };
}

/**
 * The config a row takes when its topic's switch changes.
 *
 * Off drops the channel and leaves the rest of the row alone. On brings back
 * what the platform ships for that channel, never wider, and a row that was
 * switched off or silenced at a frequency of Off comes back able to deliver.
 *
 * @param event - the grid row.
 * @param config - its current config.
 * @param topicChannel - the switch that changed.
 * @param on - the new state of that switch.
 * @param tenantDefault - the tenant-wide cadence, or `''` when none is set.
 * @returns the config to stage.
 */
export function topicChannelConfig(
  event: SetupEvent,
  config: EventConfig,
  topicChannel: TopicChannel,
  on: boolean,
  tenantDefault: NotificationTenantDefaultFrequency,
): EventConfig {
  const belongs = (channel: NotificationChannel): boolean =>
    onTopicChannel(channel, topicChannel);
  if (!on) return { ...config, channels: config.channels.filter((c) => !belongs(c)) };

  const shipped = event.defaults.channels.filter(belongs);
  const wanted = shipped.length ? shipped : event.availableChannels.filter(belongs);
  // A row that was off comes back on this channel alone, not on what it held.
  const kept = config.enabled ? config.channels : [];
  const next: EventConfig = {
    ...config,
    enabled: true,
    channels: event.availableChannels.filter(
      (channel) => kept.includes(channel) || wanted.includes(channel),
    ),
  };
  if (topicChannel === 'bell') return next;
  const silenced = isEmailSilenced(
    resolveFrequency(next.frequency, tenantDefault, event.platformFrequency),
  );
  return silenced ? { ...next, frequency: null } : next;
}
